import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Clock, Package } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { validatePincode } from '../utils/pincodeValidator';
import { deliveryCharge } from '../mockData';
import SuccessModal from '../components/SuccessModal';
import './Checkout.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pincodeError, setPincodeError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    // Don't redirect if payment was just completed
    if (paymentCompleted) {
      return;
    }
    
    // Redirect if cart is empty
    if (cartItems.length === 0) {
      navigate('/');
      return;
    }

    // Pre-fill with user data if authenticated
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
      
      // Auto-select default address if available
      const defaultAddr = user.addresses?.find(addr => addr.isDefault);
      if (defaultAddr && !selectedAddress) {
        setSelectedAddress(defaultAddr);
        setFormData(prev => ({
          ...prev,
          street: defaultAddr.street,
          city: defaultAddr.city,
          state: defaultAddr.state,
          pincode: defaultAddr.pincode
        }));
      }
    }
  }, [cartItems, isAuthenticated, user, navigate, paymentCompleted, selectedAddress]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'pincode') {
      setPincodeError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Checkout form submitted');
    
    // Validate pincode asynchronously
    const pincodeValidation = await validatePincode(formData.pincode);
    if (!pincodeValidation.valid) {
      setPincodeError(`Sorry, we don't deliver to pincode ${formData.pincode}.`);
      console.log('Pincode validation failed');
      return;
    }

    setLoading(true);
    console.log('Creating order...');

    try {
      const cartTotal = getCartTotal() + deliveryCharge;
      console.log('Cart total:', cartTotal);
      
      const orderData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode
        },
        totalAmount: cartTotal,
        items: cartItems
      };

      console.log('Order data:', orderData);
      const response = await axios.post(`${API}/orders/onetime/create-order`, orderData);
      const { razorpayOrderId, amount, currency } = response.data;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount,
        currency: currency,
        name: 'House of Microgreens',
        description: 'One-Time Order',
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            console.log('Payment successful, verifying...', response);
            const payload = {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              customerDetails: formData,
              items: cartItems
            };
            console.log('Sending verification payload:', JSON.stringify(payload, null, 2));
            
            const verifyData = await axios.post(`${API}/orders/onetime/verify-payment`, payload, {
              withCredentials: true
            });

            console.log('Verification successful:', verifyData);
            setPaymentCompleted(true);
            setShowSuccessModal(true);
            clearCart();
            
            // Don't auto-redirect - let user close modal or click "View Orders"
          } catch (error) {
            console.error('Payment verification error:', error);
            console.error('Error response:', error.response);
            console.error('Error data:', error.response?.data);
            
            let errorMsg = 'Payment verification failed. ';
            if (error.response?.data?.detail) {
              if (typeof error.response.data.detail === 'string') {
                errorMsg += error.response.data.detail;
              } else {
                errorMsg += JSON.stringify(error.response.data.detail);
              }
            } else if (error.message) {
              errorMsg += error.message;
            } else {
              errorMsg += 'Unknown error occurred';
            }
            errorMsg += '. Please contact support with your payment details.';
            
            alert(errorMsg);
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function() {
            console.log('Razorpay modal closed');
            setLoading(false);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#4ade80'
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        setLoading(false);
        alert(`Payment failed: ${response.error.description || 'Please try again'}`);
      });
      razorpayInstance.open();
    } catch (error) {
      console.error('Checkout error:', error);
      console.error('Error details:', error.response?.data);
      alert(`Failed to create order: ${error.response?.data?.detail || error.message}`);
      setLoading(false);
    }
  };

  const subtotal = getCartTotal();
  const total = subtotal + deliveryCharge;

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        {/* Header */}
        <div className="checkout-header">
          <button className="btn-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            Back
          </button>
          <h1>Checkout</h1>
        </div>

        <div className="checkout-grid">
          {/* Left: Form */}
          <div className="checkout-form-section">
            <h2>Delivery Details</h2>
            {!isAuthenticated && (
              <div className="signin-note" data-testid="signin-note-checkout">
                <AlertCircle size={16} />
                <span><a href="/login" className="signin-link" onClick={(e) => { e.preventDefault(); navigate('/login', { state: { from: { pathname: '/checkout' } } }); }}>Sign in</a> to track your orders and manage cancellations</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="checkout-form">
              {/* Saved Addresses */}
              {user?.addresses && user.addresses.length > 0 && (
                <div className="saved-addresses-section">
                  <label className="section-label">Select Delivery Address:</label>
                  <div className="address-options-grid">
                    {user.addresses.map((addr) => (
                      <div 
                        key={addr.id} 
                        className={`address-card ${selectedAddress?.id === addr.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedAddress(addr);
                          setFormData(prev => ({
                            ...prev,
                            street: addr.street,
                            city: addr.city,
                            state: addr.state,
                            pincode: addr.pincode
                          }));
                        }}
                      >
                        <div className="address-card-content">
                          <div className="address-text">
                            {addr.street}, {addr.city}, {addr.state} - {addr.pincode}
                          </div>
                          {addr.isDefault && <span className="default-badge">Default</span>}
                        </div>
                        {selectedAddress?.id === addr.id && (
                          <div className="selected-checkmark">✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="helper-text">Or fill in a new address below</p>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="10-digit mobile number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="street">Address *</label>
                <input
                  type="text"
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  required
                  placeholder="Street address, apartment, suite, etc."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    placeholder="Bangalore"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State *</label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    placeholder="Karnataka"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="pincode">Pincode *</label>
                <input
                  type="text"
                  id="pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  required
                  placeholder="6-digit pincode"
                  maxLength="6"
                />
                {pincodeError && (
                  <div className="form-error">
                    <AlertCircle size={16} />
                    <span>{pincodeError}</span>
                  </div>
                )}
              </div>

              <button 
                type="submit" 
                className="btn-checkout-submit" 
                disabled={loading}
              >
                {loading ? 'Processing...' : `Pay ₹${total}`}
              </button>
            </form>
          </div>

          {/* Right: Order Summary */}
          <div className="checkout-summary-section">
            <div className="summary-card">
              <h2>Order Summary</h2>
              
              <div className="summary-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="summary-item">
                    <img src={item.image} alt={item.name} />
                    <div className="summary-item-details">
                      <h4>{item.name}</h4>
                      <p>Qty: {item.quantity} × ₹{item.price}</p>
                    </div>
                    <span className="summary-item-total">₹{item.quantity * item.price}</span>
                  </div>
                ))}
              </div>

              <div className="summary-totals">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>₹{subtotal}</span>
                </div>
                <div className="summary-row">
                  <span>Delivery Charge</span>
                  <span>₹{deliveryCharge}</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>₹{total}</span>
                </div>
              </div>

              <div className="summary-info">
                <div className="info-item">
                  <Clock size={18} />
                  <span>Delivery in 7-10 days</span>
                </div>
                <div className="info-item">
                  <Package size={18} />
                  <span>Freshly grown microgreens</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SuccessModal 
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/dashboard/orders');
        }}
      />
    </div>
  );
};

export default Checkout;
